class FacetFiltersForm extends HTMLElement {
  // Static property to store unit preferences for filters
  static filterUnitStates = {};

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Add event listener for the unit toggle button clicks
    this.addEventListener('click', this.handleUnitToggle.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Add a direct document listener for mobile unit toggle buttons
    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('.mobile-facets__unit-toggle');
      if (!toggleButton) return;

      // Stop event from reaching other handlers
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      const containerId = toggleButton.dataset.unitToggleFor;
      if (!containerId) return;

      const detailsElement = toggleButton.closest('.js-filter[data-filter-param]');
      const filterParam = detailsElement ? detailsElement.dataset.filterParam : null;

      const container = document.querySelector(`[data-unit-container="${containerId}"]`);
      if (!container) return;

      const currentUnit = container.dataset.currentUnit || 'mm';
      const newUnit = currentUnit === 'mm' ? 'in' : 'mm';

      if (filterParam) {
        FacetFiltersForm.filterUnitStates[filterParam] = newUnit;
      }

      FacetFiltersForm.applyUnitConversion(filterParam, newUnit);

      return false;
    }, true); // Using capture phase to ensure we get the event first
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // After all DOM updates in renderFilters, re-apply any persisted unit states
    FacetFiltersForm.applyPersistedUnitStates();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  // Instance method to handle unit toggling
  handleUnitToggle(event) {
    const toggleButton = event.target.closest('.facets__unit-toggle');
    if (!toggleButton) return;

    // Handle mobile toggle differently from desktop
    const isMobileToggle = toggleButton.classList.contains('mobile-facets__unit-toggle');

    // For all toggle buttons, prevent default action
    event.preventDefault();

    // For mobile toggles, we need to be more aggressive with stopping event propagation
    if (isMobileToggle) {
      // Stop immediate propagation to prevent any other handlers from executing
      event.stopImmediatePropagation();
      event.stopPropagation();
    }

    const containerId = toggleButton.dataset.unitToggleFor;
    if (!containerId) return;

    const detailsElement = toggleButton.closest('.js-filter[data-filter-param]');
    const filterParam = detailsElement ? detailsElement.dataset.filterParam : null;

    // Handle mobile and desktop containers differently if needed
    const container = document.querySelector(`[data-unit-container="${containerId}"]`);
    if (!container) return;

    const currentUnit = container.dataset.currentUnit || 'mm';
    const newUnit = currentUnit === 'mm' ? 'in' : 'mm';

    if (filterParam) {
      FacetFiltersForm.filterUnitStates[filterParam] = newUnit;
    }

    // Apply the unit conversion on proper container
    FacetFiltersForm.applyUnitConversion(filterParam, newUnit);

    // For mobile, prevent default on the closest button to stop drawer closing
    if (isMobileToggle) {
      // Find all parent buttons that might be triggered and prevent their action
      const parentButtons = toggleButton.closest('.mobile-facets__submenu')
        ?.querySelectorAll('button:not(.facets__unit-toggle)');

      if (parentButtons) {
        parentButtons.forEach(btn => {
          const originalClick = btn.onclick;
          btn.onclick = function (e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          };
          // Restore original click after a short delay
          setTimeout(() => {
            btn.onclick = originalClick;
          }, 100);
        });
      }

      // Return false to ensure the event doesn't propagate
      return false;
    }
  }

  // Enhance the applyUnitConversion method to better handle mobile elements
  static applyUnitConversion(filterParam, newUnit) {
    if (!filterParam) return;

    // Find both desktop and mobile containers
    const containers = document.querySelectorAll(`[data-unit-container*="${filterParam}"]`);
    if (containers.length === 0) return;

    const newButtonText = newUnit === 'in' ? 'Switch to Millimeters' : 'Switch to Inches';

    containers.forEach(container => {
      const labels = container.querySelectorAll('.facet-checkbox__text-label[data-value-mm]');
      labels.forEach(label => {
        const mmValue = label.dataset.valueMm;
        if (!mmValue) return;

        if (newUnit === 'in') {
          const inchesValue = (parseFloat(mmValue) / 25.4).toFixed(1);
          label.textContent = `${inchesValue}"`;
        } else {
          label.textContent = `${mmValue}`;
        }
      });
      // Update the state on the container
      container.dataset.currentUnit = newUnit;
    });

    // Update all toggle buttons
    const allToggleButtons = document.querySelectorAll(`[data-unit-toggle-for*="${filterParam}"]`);
    allToggleButtons.forEach(btn => btn.textContent = newButtonText);
  }

  // New static method to re-apply states after re-render
  static applyPersistedUnitStates() {
    for (const filterParam in FacetFiltersForm.filterUnitStates) {
      const desiredUnit = FacetFiltersForm.filterUnitStates[filterParam];
      // Check if the filter still exists in the DOM
      const filterExists = document.querySelector(`.js-filter[data-filter-param="${filterParam}"]`);
      if (filterExists && desiredUnit === 'in') {
        // Re-apply the 'in' conversion visually
        FacetFiltersForm.applyUnitConversion(filterParam, 'in');
      } else if (!filterExists) {
        // Clean up state if filter no longer exists
        delete FacetFiltersForm.filterUnitStates[filterParam];
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();



class PriceRange extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.rangeInputs = this.querySelectorAll('.range-input input');
    this.rangeSlider = this.querySelector('.slider-container .price-slider');
    this.minPriceText = this.querySelector('.min_price');
    this.maxPriceText = this.querySelector('.max_price');
    this.currencySymbol = this.querySelector('.price-range-main').getAttribute('currency-symbol');
    this.minPriceInput = this.querySelector('.min-price-input');
    this.maxPriceInput = this.querySelector('.max-price-input');
    this.maxPriceLimit = parseInt(this.rangeInputs[1].max, 10);

    this.init();
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMin = urlParams.get('filter.v.price.gte');
    const urlMax = urlParams.get('filter.v.price.lte');

    const minVal = urlMin ? parseInt(urlMin, 10) : parseInt(this.rangeInputs[0].value, 10);
    const maxVal = urlMax ? parseInt(urlMax, 10) : parseInt(this.rangeInputs[1].value, 10);

    const validMaxVal = Math.min(maxVal, this.maxPriceLimit);

    this.updateUI(minVal, validMaxVal);
    this.bindEvents();
  }

  bindEvents() {
    this.rangeInputs.forEach((input, index) => {
      input.addEventListener('input', () => {
        let newMin = parseInt(this.rangeInputs[0].value, 10);
        let newMax = parseInt(this.rangeInputs[1].value, 10);

        // Prevent values from crossing each other
        if (index === 0) {
          // If min input is being changed
          newMin = Math.min(newMin, newMax);
          this.rangeInputs[0].value = newMin;
        } else {
          // If max input is being changed
          newMax = Math.max(newMax, newMin);
          this.rangeInputs[1].value = newMax;
        }

        const validMax = Math.min(newMax, this.maxPriceLimit);
        this.updateUI(newMin, validMax);
      });
      input.addEventListener('change', () => {
        this.triggerFormSubmission();
      });
    });

    if (this.minPriceInput) {
      this.minPriceInput.addEventListener('input', () => {
        const newMin = parseInt(this.minPriceInput.value, 10) || 0;
        const currentMax = parseInt(this.rangeInputs[1].value, 10);

        // Prevent min from exceeding max
        const validMin = Math.min(newMin, currentMax);
        this.minPriceInput.value = validMin;

        this.updateUI(validMin, currentMax);
      });

      this.minPriceInput.addEventListener('change', () => {
        this.triggerFormSubmission();
      });
    }

    if (this.maxPriceInput) {
      this.maxPriceInput.addEventListener('input', () => {
        const currentMin = parseInt(this.rangeInputs[0].value, 10);
        let newMax = parseInt(this.maxPriceInput.value, 10) || currentMin;

        // Prevent max from being lower than min
        newMax = Math.max(newMax, currentMin);
        newMax = Math.min(newMax, this.maxPriceLimit);
        this.maxPriceInput.value = newMax;

        this.updateUI(currentMin, newMax);
      });

      this.maxPriceInput.addEventListener('change', () => {
        this.triggerFormSubmission();
      });
    }
  }

  updateUI(min, max) {
    min = Math.max(0, min);
    max = Math.min(max, this.maxPriceLimit);

    this.rangeSlider.style.left = `${(min / this.maxPriceLimit) * 100}%`;
    this.rangeSlider.style.right = `${100 - (max / this.maxPriceLimit) * 100}%`;

    this.rangeInputs[0].value = min;
    this.rangeInputs[1].value = max;

    if (this.minPriceInput) {
      this.minPriceInput.value = min;
    }

    if (this.maxPriceInput) {
      this.maxPriceInput.value = max;
    }
  }

  triggerFormSubmission() {
    const form = this.closest('form');
    if (form) {
      const inputEvent = new Event('input', { bubbles: true });
      form.dispatchEvent(inputEvent);
    }
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
